import { Formik } from 'formik';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClinicsList } from '../../../../api/clinics';
import { setClinicsList } from '../../../../store/clinicsSlice';
import { RootState } from '../../../../store/store';
import Loader from '../../../BaseComponents/Loader';
import Modal from '../../../BaseComponents/Modal';
import AppointmentForm from './AppointmentForm';
import {
  createAppointment,
  getAppointment,
  updateAppointment
} from '../../../../api/appointments';
import {
  AppointmentStatus,
  AppointmentAssigneeType,
  LocationType
} from '../../../../types/apiGeneratedTypes';
import CheckIcon from '../../../../assets/icons/color-check.webp';
import Button from '../../../BaseComponents/Button';
import {
  setAppointmentIdToEdit,
  setUpdatedAppointment
} from '../../../../store/memberSlice';
import { getDoctorsList } from '../../../../api/doctors';
import { getNutritionistsList } from '../../../../api/nutritionist';
import { waitForMinModalLoaderVisible } from '../../../../utils/modalLoaderMinVisible';


type IProps = {
  isOpen: boolean;
  onClose: () => void;
  onAddAppointmentCreate: () => void;
  isAppointmentView: boolean
};

export interface FormValues {
  clinic: { key: any; value: string };
  meeting_link: string | null | undefined;
  time: any;
  title: string;
  preparation_text: string;
  status: AppointmentStatus | null;
  assignee_type: AppointmentAssigneeType;
  assignee: { key: string; value: string };
}

const AppointmentModal = ({
  isOpen,
  onClose,
  onAddAppointmentCreate,
  isAppointmentView = false
}: IProps) => {
  const dispatch = useDispatch();
  const [currentLocationOption, setCurrentLocationOption] =
    React.useState<LocationType>(LocationType.CLINIC);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<{ payload: string } | null>(
    null
  );
  const [isFormError, setIsFormError] = useState<string | null>(null);
  const [formInitialValues, setFormInitialValues] = useState<FormValues>({
    clinic: { key: '', value: '' },
    time: null,
    meeting_link: '',
    title: '',
    preparation_text: '',
    status: AppointmentStatus.DATE_SCHEDULED,
    assignee_type: AppointmentAssigneeType.DOCTOR,
    assignee: { key: '', value: '' },
  });
  const { memberData } = useSelector((state: RootState) => state.member);
  const { clinicsList } = useSelector((state: RootState) => state.clinics);
  const { appointmentIdToEdit } = useSelector(
    (state: RootState) => state.member
  );
  const [doctorsOptions, setDoctorsOptions] = useState<{ key: string; value: string }[]>([]);
  const [nutritionistsOptions, setNutritionistsOptions] = useState<{ key: string; value: string }[]>([]);

  const defaultDoctorAssignee = useMemo(() => {
    const d = memberData?.doctor;
    if (!d?.user_id) return null;
    return { key: d.user_id, value: `${d.first_name} ${d.last_name}` };
  }, [memberData?.doctor]);

  const defaultNutritionistAssignee = useMemo(() => {
    const n = memberData?.nutritionist;
    if (!n?.user_id) return null;
    return { key: n.user_id, value: `${n.first_name} ${n.last_name}` };
  }, [memberData?.nutritionist]);

  const isCoreMember = memberData?.membership_type?.toLowerCase() === 'core';

  // If Core member has Scan selected, switch to CLINIC
  useEffect(() => {
    if (isCoreMember && currentLocationOption === LocationType.SCAN) {
      setCurrentLocationOption(LocationType.CLINIC);
    }
  }, [isCoreMember, currentLocationOption]);

  const onLocationBtnClick = (location: LocationType) => {
    // Prevent Core members from selecting Scan
    if (isCoreMember && location === LocationType.SCAN) {
      return;
    }
    if (currentLocationOption !== location) {
      setCurrentLocationOption(location);
    }
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      return;
    }
    if (appointmentIdToEdit) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isOpen, appointmentIdToEdit]);

  useEffect(() => {
    if (appointmentIdToEdit) {
      const fetchAppointmentToEdit = async () => {
        const loaderStartedAt = Date.now();
        try {
          const appointmentToEdit = await getAppointment(appointmentIdToEdit);
          setFormInitialValues({
            clinic: {
              key: appointmentToEdit.clinic?.id,
              value: appointmentToEdit.clinic?.title!
            },
            time: appointmentToEdit.scheduled_time
              ? new Date(appointmentToEdit.scheduled_time)
              : null,
            meeting_link: appointmentToEdit.meeting_url || '',
            title: appointmentToEdit.title,
            preparation_text: appointmentToEdit.preparation_text || '',
            status: appointmentToEdit.status,
            assignee_type:
              appointmentToEdit.assignee_type || AppointmentAssigneeType.DOCTOR,
            assignee: appointmentToEdit.assignee_id
              ? { key: appointmentToEdit.assignee_id, value: '' }
              : { key: '', value: '' },
          });
          if (appointmentToEdit.location) {
            // If Core member and appointment is Scan type, switch to CLINIC
            if (isCoreMember && appointmentToEdit.location === LocationType.SCAN) {
              setCurrentLocationOption(LocationType.CLINIC);
            } else {
              setCurrentLocationOption(appointmentToEdit.location);
            }
          }
        } catch (error) {
          setError(true);
        } finally {
          if (isAppointmentView) {
            await waitForMinModalLoaderVisible(loaderStartedAt);
          }
          setIsLoading(false);
        }
      };
      fetchAppointmentToEdit();
    }
  }, [appointmentIdToEdit, isAppointmentView, isCoreMember]);

  useEffect(() => {
    // Resolve stored assignee id into a human-readable name for the dropdown.
    setFormInitialValues((prev) => {
      const key = prev.assignee?.key;
      if (!key) return prev;
      if (prev.assignee?.value && prev.assignee.value !== key) return prev;

      const doctorMatch = doctorsOptions.find((o) => String(o.key) === String(key));
      const nutritionistMatch = nutritionistsOptions.find(
        (o) => String(o.key) === String(key)
      );

      // If assignee_type is missing/incorrect, infer it from which list contains the id.
      if (nutritionistMatch) {
        return {
          ...prev,
          assignee_type: AppointmentAssigneeType.NUTRITIONIST,
          assignee: nutritionistMatch,
        };
      }
      if (doctorMatch) {
        return {
          ...prev,
          assignee_type: AppointmentAssigneeType.DOCTOR,
          assignee: doctorMatch,
        };
      }

      return prev;
    });
  }, [doctorsOptions, nutritionistsOptions, setFormInitialValues]);

  const isValidURL = (value: string) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.status) {
      errors.status = 'Status is required';
    }
    if (values.meeting_link && !isValidURL(values.meeting_link)) {
      errors.meeting_link = 'Invalid URL';
    }
    if (currentLocationOption === LocationType.ONLINE) {
      if (!values.assignee?.key) {
        errors.assignee = 'Assignee is required';
      }
    }

    return errors;
  };

  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const [doctorsRes, nutritionistsRes] = await Promise.all([
          getDoctorsList(0, 1000),
          getNutritionistsList(0, 1000),
        ]);
        setDoctorsOptions(
          (doctorsRes.doctors || []).map((d: any) => ({
            // API appointment.assignee_id is a user UUID (user_id), so prefer that.
            key: String(d.user_id ?? d.id),
            value: `${d.first_name} ${d.last_name}`,
          }))
        );
        setNutritionistsOptions(
          (nutritionistsRes.nutritionists || []).map((n: any) => ({
            // API appointment.assignee_id is a user UUID (user_id), so prefer that.
            key: String(n.user_id ?? n.id),
            value: `${n.first_name} ${n.last_name}`,
          }))
        );
      } catch (e) {
        // Non-fatal: user can still keep default assignee
      }
    };
    fetchAssignees();
  }, []);

  useEffect(() => {
    if (!memberData) return;

    const defaultAssigneeType =
      memberData?.doctor?.user_id
        ? AppointmentAssigneeType.DOCTOR
        : memberData?.nutritionist?.user_id
          ? AppointmentAssigneeType.NUTRITIONIST
          : AppointmentAssigneeType.DOCTOR;

    const defaultAssignee =
      defaultAssigneeType === AppointmentAssigneeType.DOCTOR
        ? memberData?.doctor
          ? {
            key: memberData.doctor.user_id,
            value: `${memberData.doctor.first_name} ${memberData.doctor.last_name}`,
          }
          : { key: '', value: '' }
        : memberData?.nutritionist
          ? {
            key: memberData.nutritionist.user_id,
            value: `${memberData.nutritionist.first_name} ${memberData.nutritionist.last_name}`,
          }
          : { key: '', value: '' };

    setFormInitialValues((prev) => ({
      ...prev,
      assignee_type: prev.assignee_type || defaultAssigneeType,
      assignee: prev.assignee?.key ? prev.assignee : defaultAssignee,
    }));
  }, [memberData]);

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        setIsLoading(true);
        const clinicsList = await getClinicsList(0, 1000);
        dispatch(setClinicsList(clinicsList.clinics));
      } catch (error) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (!clinicsList?.length) {
      fetchClinics();
    }
  }, [isOpen, clinicsList?.length, dispatch]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setIsSubmitted(null);
        dispatch(setAppointmentIdToEdit(null));
        setFormInitialValues({
          clinic: { key: '', value: '' },
          time: null,
          meeting_link: '',
          title: '',
          preparation_text: '',
          status: AppointmentStatus.DATE_SCHEDULED,
          assignee_type: AppointmentAssigneeType.DOCTOR,
          assignee: { key: '', value: '' },
        });
        onClose();
      }}
      title={
        isSubmitted
          ? undefined
          : isAppointmentView ?
            'View Appointment' :
            appointmentIdToEdit
              ? 'Update Appointment'
              : 'Add Appointment'
      }
      memberName={
        !isSubmitted
          ? `${memberData?.first_name} ${memberData?.last_name}`
          : undefined
      }
      width={!isSubmitted ? '700px' : '400px'}
    >
      <div className='appointments-modal-container'>
        {error ? (
          <div>Error loading data...</div>
        ) : isLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200
            }}
          >
            <Loader />
          </div>
        ) : (
          <>
            {!isSubmitted ? (
              <Formik
                initialValues={formInitialValues}
                validate={validate}
                onSubmit={async (values, { resetForm }) => {
                  setIsSubmitting(true);
                  setError(false);

                  try {
                    let requestObject: any;
                    if (memberData) {
                      requestObject = {
                        title: values.title,
                        location:
                          currentLocationOption.toUpperCase() as LocationType,
                        clinic_id:
                          currentLocationOption === LocationType.CLINIC ||
                            currentLocationOption === LocationType.HISTORICRESULTS ||
                            currentLocationOption === LocationType.SCAN ||
                            currentLocationOption === LocationType.DEXA
                            ? (values.clinic.key || null)
                            : null,
                        scheduled_time: values.time.toISOString(),
                        meeting_url:
                          currentLocationOption === LocationType.ONLINE
                            ? values.meeting_link
                            : null,
                        member_id: memberData.user_id,
                        preparation_text: values.preparation_text,
                        status: values.status,
                        assignee:
                          currentLocationOption === LocationType.ONLINE
                            ? { type: values.assignee_type, id: values.assignee.key }
                            : null,
                      };
                    }

                    if (requestObject) {
                      if (appointmentIdToEdit) {
                        await updateAppointment({
                          ...requestObject,
                          appointment_id: appointmentIdToEdit
                        });
                        dispatch(
                          setUpdatedAppointment({
                            ...requestObject,
                            id: appointmentIdToEdit
                          })
                        );
                        dispatch(setAppointmentIdToEdit(null));
                        onAddAppointmentCreate();
                      } else {
                        await createAppointment(requestObject);
                        onAddAppointmentCreate();
                      }
                    }

                    dispatch(setAppointmentIdToEdit(null));
                    setIsSubmitted({ payload: 'updated' });
                    resetForm();
                  } catch (error: any) {
                    setIsFormError(error.message);
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize={true}
              >
                <AppointmentForm
                  currentLocationOption={currentLocationOption}
                  onLocationBtnClick={onLocationBtnClick}
                  isFormError={isFormError}
                  isSubmitting={isSubmitting}
                  membershipType={memberData?.membership_type}
                  doctorsOptions={doctorsOptions}
                  nutritionistsOptions={nutritionistsOptions}
                  defaultDoctorAssignee={defaultDoctorAssignee}
                  defaultNutritionistAssignee={defaultNutritionistAssignee}
                  isAppointmentView={isAppointmentView}
                />
              </Formik>
            ) : (
              <div className='success-message'>
                <img src={CheckIcon} alt='check' className='check' />
                <span className='subtext'>
                  Your appointment was <br />{' '}
                  {isSubmitted.payload === 'updated' ? 'updated' : 'scheduled'}{' '}
                  successfully
                </span>
                <Button
                  text='Add new appointment'
                  onClick={() => setIsSubmitted(null)}
                />
                <div
                  onClick={() => {
                    setIsSubmitted(null);
                    onClose();
                  }}
                  className='close-button'
                >
                  Go to Member's page
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default AppointmentModal;
